import { useRouter } from 'next/router';
import { useState, useEffect } from 'react';
import Dynamic from '@base/Dynamic/Dynamic';
import usePrimePracticeQuota from '@hooks/usePrimePracticeQuota';
import { PrimeTrialPromoContextualId } from '@stores/prime-pitch/types';
import { fetchPracticeByEducators } from '@api/fetchers/practice';
import styled from '@emotion/styled';
import usePrimeQuotaStore from '@stores/prime-quota/store';
import { UserPlatformPlan } from '@stores/prime-quota/types';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsSubscribedToGoal } from '@api/selectors/subscription';
import { getIsLoggedIn } from '@api/selectors/auth';
import PrimeBottomBanner from '@cont/PrimeHome/PrimeBottomBanner';
import { readCookie } from '@utils/cookies';
import Header from './Header/Header';
import Reminder from './Reminder/Reminder';
import Content from './Content';
import PracticeByEducators from '../AutoDpp/PracticeByEducators';
import EducatorLeaderboard from './EducatorLeaderboard/EducatorLeaderboard';

const Modals = Dynamic(() => import('./Modals'));

export const BottomSpacer = styled.div`
  grid-column: 1 / -1;
  height: 30px;

  @media screen and (max-width: 768px) {
    height: 60px;
  }
`;
const getGoalUIDFromQuery = (query) => {
  const goalUID = query?.goalUID;
  return Array.isArray(goalUID) ? goalUID[0] : goalUID;
};

const getGoalSlugFromQuery = (query) => {
  const goalSlug = query?.goalSlug;
  return Array.isArray(goalSlug) ? goalSlug[0] : goalSlug;
};

const Landing = ({ practicesByEducators: ssrPractices }) => {
  const router = useRouter();
  const goalUID = getGoalUIDFromQuery(router.query);
  const goalSlug = getGoalSlugFromQuery(router.query);

  // Fetch educator practices on client-side to avoid SSR API issues
  const [practicesByEducators, setPracticesByEducators] = useState(
    ssrPractices || { dppCards: [] }
  );

  useEffect(() => {
    const fetchEducatorPractices = async () => {
      if (goalUID) {
        const accessToken = readCookie('accessToken');
        const data = await fetchPracticeByEducators(goalUID, accessToken);
        if (data && data.dppCards) {
          setPracticesByEducators(data);
        }
      }
    };

    fetchEducatorPractices();
  }, [goalUID]);

  // Only fetch quota when goalUID is available
  const { primePracticeQuota } = usePrimePracticeQuota(goalUID);

  const practicesLeft = primePracticeQuota?.left ?? 0;
  const totalPractices = primePracticeQuota?.total ?? 0;
  const isPlusUser = useMyInfo(getIsSubscribedToGoal(goalUID));
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const hasPrimePaidPlan =
    usePrimeQuotaStore((state) => state.userPlatformPlan) ===
    UserPlatformPlan.PAID;
  const shouldShowBottomBanner = isLoggedIn && !isPlusUser && !hasPrimePaidPlan;
  return (
    <>
      <Header />
      <Reminder />
      {practicesByEducators?.dppCards?.length > 0 && (
        <PracticeByEducators data={practicesByEducators} />
      )}
      <EducatorLeaderboard />
      <Content />
      <Modals />
      {shouldShowBottomBanner && <BottomSpacer />}
      <PrimeBottomBanner
        title={
          practicesLeft <= 0
            ? 'Daily limit reached'
            : `${practicesLeft} out of ${totalPractices} free questions left!`
        }
        subtitle="Get unlimited access with Prime."
        contextualId={PrimeTrialPromoContextualId.QUESTION_QUOTA}
        goalUID={goalUID}
        goalSlug={goalSlug}
      />
    </>
  );
};

export default Landing;
